import { useQuery } from "@apollo/client"
import { type QueryResult } from "@apollo/client/react"
import { Dialog } from "@headlessui/react"
import cn from "classnames"
import { motion, AnimatePresence } from "framer-motion"
import { debounce } from "lodash-es"
import React, { useRef, useEffect, useState, useCallback, useMemo, memo, type CSSProperties } from "react"
import { Trans, useTranslation } from "react-i18next"
import AutoSizer from "react-virtualized-auto-sizer"
import { FixedSizeList, areEqual, type ListChildComponentProps } from "react-window"
import InfiniteLoader from "react-window-infinite-loader"
import { type FragmentType, getFragment, graphql } from "@/api"
import { type BasketStatusQuery, type BasketItemsQuery } from "@/api/graphql"
import ClientData from "@/common/client_data"
import ConfirmLink from "@/common/components/confirm_link"
import DotSpinner from "@/common/components/dot_spinner"
import env from "@/common/env"
import storageLocal from "@/common/storage"
import { transitionTime } from "@/common/util"
import BasketSignItem, { BasketItemFragmentDoc } from "@/menu/components/basket_item"
import { CartFill, CloseLarge, CheckmarkFill } from "@/skylticons"
import "../styles/basket_modal.css"

export const basketStateDoc = graphql(`
  query BasketState {
    basketState @client {
      open
      addedNotice
      excelImportNotice
      reload
    }
  }
`)

export const setBasketStateDoc = graphql(`
  mutation SetBasketState($open: Boolean!, $addedNotice: Boolean, $excelImportNotice: Boolean, $reload: Boolean) {
    setBasketState(open: $open, addedNotice: $addedNotice, excelImportNotice: $excelImportNotice, reload: $reload)
      @client {
      open
    }
  }
`)

export const BasketFragmentDoc = graphql(`
  fragment BasketModal_Order on Order {
    id
    discountPercent
    deliveryAndShippingLocalTotal
    deliveryAndShippingTaxedTotal
    localDiscountAmount
    taxedDiscountAmount
    localPrice
    localTax
    vatPercent
    localTotal
    traceable
    generatedSigns
    basketItemsLocalTotal
    basketItemsTaxedTotal
    discountConverted
    couponCode

    basketItems {
      id
      totalCount
      hasDiscount
      localTotal
      localTaxedTotal
      regularLocalTotal
      regularTaxedTotal
    }

    deliveryFees {
      localTotal
      __typename
    }
  }
`)

export const basketStatusDoc = graphql(`
  query BasketStatus($id: ID) {
    basketStatus(id: $id) {
      ...BasketModal_Order
    }
  }
`)

const basketItemsDoc = graphql(`
  query BasketItems($limit: Int, $offset: Int, $totalCount: Int, $id: ID) {
    basketStatus(id: $id) {
      id

      basketItems(limit: $limit, offset: $offset, totalCount: $totalCount) {
        ...BasketItem_OrderItem
      }
    }
  }
`)

interface RowProps {
  items: FragmentType<typeof BasketItemFragmentDoc>[]
  index: number
  style: CSSProperties
  businessRole: boolean
  desktop: boolean
  count: number
  basketStatusFragment: FragmentType<typeof BasketFragmentDoc> | null
  toggleBasket: () => void
  itemAdded: boolean
  setItemAdded: (value: boolean) => void
  scrollCompleted: boolean
  basketItemsIds: string[]
  activePage: string
  numberOfProducts: number
  setOffset: (offset: number) => void
}

const Row: React.FC<RowProps> = ({
  items,
  index,
  style,
  businessRole,
  desktop,
  count,
  basketStatusFragment,
  toggleBasket,
  itemAdded,
  setItemAdded,
  scrollCompleted,
  basketItemsIds,
  activePage,
  numberOfProducts,
  setOffset,
}) => {
  const item = items.find(i => getFragment(BasketItemFragmentDoc, i).id === basketItemsIds[index]) || items[index]

  if (!item) {
    return (
      <div
        style={style}
        className="item product-row desktop flex h-[88px] w-full px-4 py-3 xl:h-[106px] xl:px-0 xl:pr-2"
      >
        <div className="mr-4 h-full w-[88px] min-w-[88px] rounded bg-white-5 xl:min-w-40"></div>
        <div className="flex h-full w-full items-center justify-center rounded bg-white-5">
          <DotSpinner />
        </div>
      </div>
    )
  }
  const itemFragment = getFragment(BasketItemFragmentDoc, item)
  const lastItem = itemFragment.id === basketItemsIds.slice(-1)[0]
  const editedItem = storageLocal.getItem("edited_item_id") === itemFragment.id

  const addedItemAnimation = {
    initial: { opacity: 0, background: "#9eeeca" },
    animate: {
      opacity: [0, 1],
      background: ["#9eeeca", "#9eeeca", "#ffffff"],
    },
    transition: {
      opacity: { duration: 0.2, delay: 0.2 },
      background: { duration: 0.3, delay: 0.8 },
    },
  }

  const editedItemAnimation = {
    initial: { opacity: 1, background: "#CCE1FD" },
    animate: {
      background: ["#CCE1FD", "#CCE1FD", "#ffffff"],
    },
    transition: {
      background: { duration: 0.3, delay: 0.8 },
    },
  }
  const skipAnimation = {
    initial: { opacity: 1, background: "#ffffff" },
  }

  const animation = () => {
    if (lastItem && itemAdded) {
      return addedItemAnimation
    } else if (editedItem) {
      return editedItemAnimation
    }

    return skipAnimation
  }

  return (
    <div style={style}>
      {(lastItem && itemAdded && scrollCompleted) || editedItem ? (
        <>
          {env.TEST ? (
            <BasketSignItem
              isLastItem={basketItemsIds.length === 1}
              key={getFragment(BasketItemFragmentDoc, item).id}
              index={index + 1}
              item={item}
              desktop={desktop}
              businessRole={businessRole}
              className={cn({ "!h-[170px]": !desktop, "!h-[138px]": desktop })}
              activePage={activePage}
              setOffset={setOffset}
            />
          ) : (
            <motion.div
              key={`basket-item-${index}`}
              style={{ transformOrigin: desktop ? "top" : "bottom" }}
              {...animation()}
              onAnimationComplete={() => {
                setItemAdded(false)
                storageLocal.removeItem("edited_item_id")
              }}
            >
              <BasketSignItem
                isLastItem={basketItemsIds.length === 1}
                key={getFragment(BasketItemFragmentDoc, item).id}
                index={index + 1}
                item={item}
                desktop={desktop}
                businessRole={businessRole}
                className={cn({ "!h-[170px]": !desktop, "!h-[138px]": desktop })}
                numberOfProducts={editedItem && !lastItem ? numberOfProducts : undefined}
                activePage={activePage}
                setOffset={setOffset}
              />
            </motion.div>
          )}
        </>
      ) : (
        <BasketSignItem
          isLastItem={basketItemsIds.length === 1}
          key={getFragment(BasketItemFragmentDoc, item).id}
          index={index + 1}
          item={item}
          desktop={desktop}
          businessRole={businessRole}
          activePage={activePage}
          className={basketItemsIds.length - 1 === index && itemAdded ? "hidden" : ""}
          numberOfProducts={!lastItem ? numberOfProducts : undefined}
          setOffset={setOffset}
        />
      )}

      {lastItem && !desktop && (
        <Summary
          basketStatus={basketStatusFragment}
          count={count}
          businessRole={businessRole}
          toggleBasket={toggleBasket}
          itemAdded={itemAdded}
          activePage={activePage}
        />
      )}
    </div>
  )
}

interface BasketItemsProps {
  desktop: boolean
  businessRole: boolean
  count: number
  basketStatusFragment: FragmentType<typeof BasketFragmentDoc> | null
  toggleBasket: () => void
  listContainerRef: React.RefObject<HTMLDivElement>
  isListLoaded: boolean
  setIsListLoaded: (value: boolean) => void
  itemAdded: boolean
  setItemAdded: (value: boolean) => void
  scrollCompleted: boolean
  basketItemsResult: QueryResult<BasketItemsQuery>
  basketItemsIds: string[]
  activePage: string
  setOffset: (offset: number) => void
}

const BasketItems: React.FC<BasketItemsProps> = ({
  desktop,
  businessRole,
  count,
  basketStatusFragment,
  toggleBasket,
  listContainerRef,
  isListLoaded,
  setIsListLoaded,
  itemAdded,
  setItemAdded,
  scrollCompleted,
  basketItemsResult,
  basketItemsIds,
  activePage,
  setOffset,
}) => {
  const numberOfProducts = basketItemsIds.length
  const basketItems =
    basketItemsResult.data?.basketStatus.basketItems || basketItemsResult.previousData?.basketStatus.basketItems || []

  const loadMoreItems = useCallback(
    async (startIndex: number, stopIndex: number) => {
      if (startIndex < 0) {
        return
      }

      try {
        await basketItemsResult.fetchMore({
          variables: {
            offset: startIndex,
            limit: stopIndex - startIndex + 1,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return previousResult

            fetchMoreResult.basketStatus.basketItems = [
              ...previousResult.basketStatus.basketItems,
              ...fetchMoreResult.basketStatus.basketItems,
            ]

            return {
              ...fetchMoreResult,
            }
          },
        })
      } catch (e) {
        console.error(e)
      }
    },
    [basketItemsResult]
  )

  const debouncedLoadMoreItems = useMemo(() => {
    return debounce((startIndex: number, stopIndex: number) => loadMoreItems(startIndex, stopIndex), 300)
  }, [loadMoreItems])

  const isItemLoaded = (index: number) => {
    if (basketItems) {
      return basketItems.some(i => getFragment(BasketItemFragmentDoc, i).id === basketItemsIds[index])
    } else {
      return false
    }
  }

  const RowMemo = memo(({ index, style }: ListChildComponentProps) => {
    return (
      <Row
        index={index}
        style={style}
        items={basketItems}
        desktop={desktop}
        businessRole={businessRole}
        count={count}
        basketStatusFragment={basketStatusFragment}
        toggleBasket={toggleBasket}
        itemAdded={itemAdded}
        setItemAdded={setItemAdded}
        scrollCompleted={scrollCompleted}
        basketItemsIds={basketItemsIds}
        activePage={activePage}
        numberOfProducts={numberOfProducts}
        setOffset={setOffset}
      />
    )
  }, areEqual)

  RowMemo.displayName = "RowMemo"

  return (
    <div className="items-table h-full w-full overflow-hidden pt-14 xl:pr-9 xl:pt-0">
      <div className="content-wrapper h-full">
        <div className="items h-full w-full overflow-hidden">
          <AutoSizer>
            {({ height, width }) => (
              <InfiniteLoader
                isItemLoaded={isItemLoaded}
                itemCount={numberOfProducts}
                loadMoreItems={debouncedLoadMoreItems}
              >
                {({ onItemsRendered, ref }) => (
                  <FixedSizeList
                    ref={ref}
                    overscanCount={6}
                    height={height}
                    itemCount={numberOfProducts}
                    itemSize={desktop ? 138 : 170}
                    width={width}
                    outerRef={listContainerRef}
                    onItemsRendered={fixedSizeListProps => {
                      if (!isListLoaded) {
                        setIsListLoaded(true)
                      }

                      onItemsRendered(fixedSizeListProps)
                    }}
                  >
                    {RowMemo}
                  </FixedSizeList>
                )}
              </InfiniteLoader>
            )}
          </AutoSizer>
        </div>
      </div>
    </div>
  )
}

interface SummaryProps {
  basketStatus: FragmentType<typeof BasketFragmentDoc> | null
  count: number
  businessRole: boolean
  toggleBasket: () => void
  itemAdded?: boolean
  activePage?: string
}

const Summary: React.FC<SummaryProps> = ({
  basketStatus: basketStatusFragment,
  count,
  businessRole,
  toggleBasket,
  itemAdded,
  activePage,
}) => {
  const basketStatus = getFragment(BasketFragmentDoc, basketStatusFragment)
  const { t } = useTranslation()
  const summaryRef = useRef<HTMLDivElement>(null)
  const checkout = activePage === "checkout"

  useEffect(() => {
    if (summaryRef.current && itemAdded) {
      summaryRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }
  }, [itemAdded])

  if (!basketStatus) {
    return
  }

  return (
    <div ref={summaryRef}>
      <div className="totals touch-none flex-col border border-white-20 bg-white-3 p-4 xl:rounded-lg xl:py-6">
        <div className="prices">
          <h4 className="headings-four mb-4 hidden xl:block">{t("checkout.summary")}</h4>
          <div
            className={cn("flex w-full justify-between", {
              "pb-0": checkout,
              "pb-6": !checkout,
            })}
          >
            <div className="labels">
              <div className="paragraph-xs xl:paragraph-sm !mb-1">
                {t("checkout.your_items")} ({count})
              </div>
              {basketStatus.deliveryAndShippingLocalTotal && (
                <div className="paragraph-xs xl:paragraph-sm !mb-1">{t("signslist.shipping")}</div>
              )}
              <div className="paragraph-sm xl:paragraph bold !mb-1">
                {basketStatus.vatPercent === 0 || businessRole ? t("order.price_excl_vat") : t("order.price_vat")}
              </div>
              <div className="paragraph-xs xl:paragraph-sm !mb-1">
                {t("order.vat")} (<span id="basket_tax">{basketStatus.vatPercent}</span>
                %)
              </div>
              {businessRole && <div className="paragraph-sm xl:paragraph bold !mb-1">{t("order.price_vat")}</div>}
            </div>
            <div className="values">
              <div className="paragraph-xs xl:paragraph-sm right !mb-1">
                {businessRole ? basketStatus.basketItemsLocalTotal : basketStatus.basketItemsTaxedTotal}
              </div>
              {basketStatus.deliveryAndShippingLocalTotal && (
                <div id="basket_delivery_price" className="paragraph-xs xl:paragraph-sm right !mb-1">
                  {businessRole
                    ? basketStatus.deliveryAndShippingLocalTotal
                    : basketStatus.deliveryAndShippingTaxedTotal}
                </div>
              )}
              {businessRole && (
                <div id="basket_total_price" className="paragraph-sm xl:paragraph bold right !mb-1">
                  {basketStatus.localPrice}
                </div>
              )}
              {!businessRole && (
                <>
                  <div id="basket_total_price_total" className="paragraph-sm xl:paragraph right bold !mb-1">
                    {basketStatus.localTotal}
                  </div>
                  <div id="basket_total_price_tax" className="paragraph-xs xl:paragraph-sm right !mb-1">
                    {basketStatus.localTax}
                  </div>
                </>
              )}
              {businessRole && (
                <>
                  <div id="basket_total_price_tax" className="paragraph-xs xl:paragraph-sm right !mb-1">
                    {basketStatus.localTax}
                  </div>
                  <div id="basket_total_price_total" className="paragraph-sm xl:paragraph right bold !mb-1">
                    {basketStatus.localTotal}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {!checkout && (
          <div className="buttons flex flex-col">
            {count > 0 && (
              <a
                id="basket_order_button"
                className="button button-primary button-medium checkout mb-3"
                href={t("route.order")}
                data-gtm="checkout-button"
              >
                {t("toolbox.checkout_button")}
              </a>
            )}
            <button
              id="basket_continue_button"
              className="button button-secondary button-medium continue mb-0 ml-0"
              onClick={toggleBasket}
              type="button"
            >
              {t("toolbox.continue_shopping")}
            </button>
          </div>
        )}
      </div>

      {basketStatus.discountPercent > 0 && (
        <div className="mt-2 hidden w-full rounded-lg border border-white-20 p-4 xl:flex xl:max-w-96">
          <div
            id="basket_discount_amount"
            className="blue-badge flex h-7 min-w-fit items-center rounded bg-blue-200 px-2 xl:h-8"
          >
            <div className="text-xs font-semibold text-blue-600 xl:text-base">
              {basketStatus.couponCode
                ? `${t("order.discount")} -${basketStatus.discountConverted}`
                : `${t("order.discount")} ${basketStatus.discountPercent}% (${basketStatus.discountConverted})`}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface ListProps {
  basketLoading: boolean
  count: number
  showNotice: boolean
  excelImportNotice?: boolean
  showEmpty?: boolean
  basketStatus: FragmentType<typeof BasketFragmentDoc> | null
  toggleBasket: () => void
  closeExcelImportNotice?: () => void
  desktop: boolean
  businessRole: boolean
  top?: number
  open: boolean
  activePage: string
  orderId?: string
  initialFocusRef?: React.RefObject<HTMLDivElement>
}

const List: React.FC<ListProps> = ({
  basketLoading,
  count,
  showNotice,
  excelImportNotice,
  basketStatus: basketStatusFragment,
  toggleBasket,
  showEmpty,
  closeExcelImportNotice,
  desktop,
  businessRole,
  top,
  open,
  activePage,
  orderId,
  initialFocusRef,
}) => {
  const basketStatus = getFragment(BasketFragmentDoc, basketStatusFragment)
  const { t } = useTranslation()

  const [isListLoaded, setIsListLoaded] = useState(false)
  const [showStickyTotals, setShowStickyTotals] = useState(false)
  const [itemAdded, setItemAdded] = useState(open && showNotice)
  const [scrollCompleted, setScrollCompleted] = useState(false)

  const basketItemsIds = basketStatus?.basketItems.map(item => item.id) || []
  const numberOfProducts = basketItemsIds.length || 0
  const [offset, setOffset] = useState(showNotice ? Math.max(0, numberOfProducts - 30) : 0)

  const basketItemsResult = useQuery(basketItemsDoc, {
    variables: {
      offset: offset,
      limit: 30,
      totalCount: count,
      id: orderId,
    },
    onError(e) {
      console.error(e)
    },
    fetchPolicy: "network-only",
    ssr: false,
  })

  const modalAnimation = {
    initial: desktop ? { translateY: -32, opacity: 0 } : { translateX: "100%" },
    animate: desktop ? { translateY: 0, opacity: 1 } : { translateX: 0 },
    transition: { duration: transitionTime(0.3), ease: "easeInOut" },
    exit: desktop ? { translateY: -32, opacity: 0 } : { translateX: "100%" },
  }

  const topSpacing = desktop ? top : 0
  const listContainerRef = useRef<HTMLDivElement>(null)

  const loading = basketLoading || !basketStatus

  useEffect(() => {
    if (isListLoaded) {
      const containerList = listContainerRef.current

      if (containerList && showNotice) {
        containerList.scrollTo({ top: containerList.scrollHeight, behavior: "instant" })

        if (env.TEST) {
          setScrollCompleted(true)
        } else {
          setTimeout(() => {
            setScrollCompleted(true)
          }, 300)
        }
      }

      if (desktop) {
        return
      }

      if (containerList) {
        if (containerList.scrollHeight - 260 > containerList.clientHeight) {
          setShowStickyTotals(true)
        }
      }
    }
  }, [isListLoaded, desktop, showNotice])

  useEffect(() => {
    if (isListLoaded) {
      const handleScroll = debounce((e: Event) => {
        const target = e.target as HTMLDivElement
        const threshold = target.scrollHeight - target.scrollTop - 260 <= target.clientHeight

        if (!desktop) {
          if (threshold) {
            setShowStickyTotals(false)
          } else if (!showStickyTotals) {
            setShowStickyTotals(true)
          }
        }
      }, 100)

      const containerList = listContainerRef.current

      if (containerList) {
        containerList.addEventListener("scroll", handleScroll)

        return () => {
          containerList.removeEventListener("scroll", handleScroll)
        }
      }
    }

    return
  }, [isListLoaded, desktop, showStickyTotals])

  if (numberOfProducts === 0 && showEmpty && !loading) {
    return (
      <AnimatePresence>
        <motion.div
          className="flex h-full w-full items-center justify-center"
          key="signomatic-basket-empty"
          {...modalAnimation}
        >
          <div
            id="signomatic-basket"
            className="list ordertable empty absolute right-0 md:!left-[unset] md:w-[400px] xl:fixed xl:mr-6"
            style={{ top: topSpacing }}
          >
            <div ref={initialFocusRef} tabIndex={-1} aria-hidden="true" />
            <div className="cart-header fixed top-0 z-10 flex h-14 w-full items-center justify-between border-b-1 border-b-white-20 pl-4 pr-2 pt-0 md:w-[400px] xl:relative xl:hidden xl:border-b-0 xl:pl-8 xl:pt-8">
              <span className="flex">
                <CartFill className="mr-1 h-6 w-6 flex-shrink-0" />
                <h4 className="headings-four bold mb-0 mr-1 w-auto">{t("signslist.basket")}</h4>
                <span className="paragraph-sm mb-0 mt-0.5">
                  {count} {count == 1 ? t("order.item") : t("checkout.items")}
                </span>
              </span>
              <button
                className="close-cart button button-subtle button-large icon-only mb-0 xl:absolute xl:right-4 xl:top-4"
                onClick={toggleBasket}
                type="button"
              >
                <CloseLarge />
              </button>
            </div>
            <div className="content mt-14 px-4 py-8 xl:mt-0">
              <p className="paragraph bold center no-max-width">{t("basket.empty")}</p>
              <p className="paragraph-xs center no-max-width">
                <Trans
                  i18nKey="basket.empty_text"
                  values={{ designpage: t("menu.signomatic") }}
                  components={{ a: <a href={`${env.languageSegment}${t("route.customer_zone")}`}>link</a> }}
                />
              </p>
              <a
                href={`${env.languageSegment}${t("route.signomatic")}`}
                className="button button-emphasis button-medium full-width mb-0"
              >
                {t("basket.cta")}
              </a>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    )
  }

  return (
    <AnimatePresence>
      <motion.div
        className="flex h-full w-full items-center justify-center"
        key="signomatic-basket"
        {...modalAnimation}
      >
        <div
          id="signomatic-basket"
          className={cn("list ordertable absolute z-50 md:!left-[unset] md:w-[400px] xl:fixed xl:mr-6", {
            loading,
            "basket-items-loading": basketItemsResult.loading,
            "basket-items-loaded": !basketItemsResult.loading && !!basketItemsResult.data?.basketStatus.id,
          })}
          style={{ top: topSpacing }}
        >
          {loading ? (
            <DotSpinner />
          ) : (
            <>
              <div ref={initialFocusRef} tabIndex={-1} aria-hidden="true" />
              <div className="cart-header fixed top-0 z-10 flex h-14 w-full items-center justify-between border-b-1 border-b-white-20 pl-4 pr-2 pt-0 md:w-[400px] xl:relative xl:w-full xl:border-b-0 xl:pl-8 xl:pt-8">
                <span className="flex">
                  <CartFill className="mr-1 h-6 w-6 flex-shrink-0" />
                  <h4 className="headings-four bold mb-0 mr-1 w-auto">{t("signslist.basket")}</h4>
                  <span className="paragraph-sm mb-0 mt-0.5">
                    {count} {count == 1 ? t("order.item") : t("checkout.items")}
                  </span>
                </span>
                <button
                  className="close-cart button button-subtle button-large icon-only mb-0 xl:absolute xl:right-4 xl:top-4"
                  onClick={toggleBasket}
                  type="button"
                >
                  <CloseLarge />
                </button>
              </div>
              {excelImportNotice && (
                <div className="z-10 mx-8 mb-3 mt-3 hidden justify-center rounded border-1 border-green-200 bg-green-100 px-4 py-3 lg:flex">
                  <CheckmarkFill className="mr-2 h-6 w-6 text-success" />
                  <div className="flex flex-col">
                    <div className="paragraph-sm bold mb-0">{t("excel_import.added_to_cart")}</div>
                    <div className="paragraph-xs mb-0">{t("excel_import.please_review")}</div>
                  </div>
                  <button
                    className="import-signs-notice-close button button-large button-subtle icon-only absolute right-12 mb-0"
                    onClick={() => closeExcelImportNotice && closeExcelImportNotice()}
                    type="button"
                  >
                    <CloseLarge />
                  </button>
                </div>
              )}
              <div className="flex h-full flex-col overflow-auto p-0 xl:flex-row xl:p-8 xl:pt-2">
                <BasketItems
                  desktop={desktop}
                  businessRole={businessRole}
                  count={count}
                  basketStatusFragment={basketStatusFragment}
                  toggleBasket={toggleBasket}
                  listContainerRef={listContainerRef}
                  isListLoaded={isListLoaded}
                  setIsListLoaded={setIsListLoaded}
                  itemAdded={itemAdded}
                  setItemAdded={setItemAdded}
                  scrollCompleted={scrollCompleted}
                  basketItemsResult={basketItemsResult}
                  basketItemsIds={basketItemsIds}
                  activePage={activePage}
                  setOffset={setOffset}
                />

                {showStickyTotals && (
                  <div className="absolute bottom-2 mx-2 flex h-16 w-[calc(100%-16px)] items-center justify-between rounded-lg border border-white-10 bg-white px-3 md:w-[384px] xl:hidden">
                    <div className="flex flex-col">
                      <h5 className="headings-five mb-1">
                        {t("checkout.total")}: {businessRole ? basketStatus.localPrice : basketStatus.localTotal}
                      </h5>
                      {!businessRole && (
                        <div className="flex">
                          <p className="paragraph-2xs mb-0">{basketStatus.localTax}</p>
                          <p className="paragraph-2xs mb-0 ml-1 whitespace-nowrap">{t("order.vat_included")}</p>
                        </div>
                      )}
                    </div>

                    <a
                      id="basket_order_button"
                      className="button button-primary button-medium checkout mb-0"
                      href={t("route.order")}
                      data-gtm="checkout-button"
                    >
                      {t("toolbox.checkout")}
                    </a>
                  </div>
                )}

                <div className="hidden w-full flex-col xl:flex xl:max-w-96">
                  <Summary
                    basketStatus={basketStatusFragment}
                    count={count}
                    businessRole={businessRole}
                    toggleBasket={toggleBasket}
                    activePage={activePage}
                  />

                  {basketStatus.generatedSigns && (
                    <div className="p-4 pl-0">
                      <ConfirmLink
                        text={t("signslist.delete_all")}
                        href={`/order/remove_all/${ClientData.orderId}`}
                        style={{ color: "red" }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

interface BasketModalProps {
  showEmpty?: boolean
  businessRole: boolean
  toggleBasket: () => void
  count: number
  loading: boolean
  showAddedNotice: boolean
  closeExcelImportNotice: () => void
  open: boolean
  excelImportNotice: boolean
  data?: BasketStatusQuery
  desktop: boolean
  top?: number
  activePage: string
  orderId?: string
}

const BasketModal: React.FC<BasketModalProps> = ({
  showEmpty,
  businessRole,
  toggleBasket,
  count,
  loading,
  showAddedNotice,
  closeExcelImportNotice,
  open,
  excelImportNotice,
  data,
  top,
  desktop,
  activePage,
  orderId,
}) => {
  const initialFocusRef = useRef(null)

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 bg-ink-100 bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: transitionTime(0.3), ease: "easeInOut" }}
            key="basket-overlay"
          />
        )}
      </AnimatePresence>

      <Dialog open={open} as="div" onClose={toggleBasket} className="z-40" initialFocus={initialFocusRef}>
        {env.TEST ? (
          <Dialog.Panel>
            <List
              basketLoading={loading}
              basketStatus={data ? data.basketStatus : null}
              showNotice={showAddedNotice}
              excelImportNotice={excelImportNotice}
              toggleBasket={toggleBasket}
              showEmpty={showEmpty}
              count={count}
              closeExcelImportNotice={closeExcelImportNotice}
              desktop={desktop}
              businessRole={businessRole}
              top={top}
              open={open}
              activePage={activePage}
              orderId={orderId}
              initialFocusRef={initialFocusRef}
            />
          </Dialog.Panel>
        ) : (
          <div className="overlay fixed inset-0 z-40">
            <Dialog.Panel>
              <List
                basketLoading={loading}
                basketStatus={data ? data.basketStatus : null}
                showNotice={showAddedNotice}
                excelImportNotice={excelImportNotice}
                toggleBasket={toggleBasket}
                showEmpty={showEmpty}
                count={count}
                closeExcelImportNotice={closeExcelImportNotice}
                desktop={desktop}
                businessRole={businessRole}
                top={top}
                open={open}
                activePage={activePage}
                orderId={orderId}
                initialFocusRef={initialFocusRef}
              />
            </Dialog.Panel>
          </div>
        )}
      </Dialog>
    </>
  )
}

export default BasketModal
